import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  FormGroup,
  Form,
  Input,
  Container,
  Row,
  Col,
  ModalFooter,
} from "reactstrap";
import Header from "components/Headers/Header";
import {
  API_BASE,
  API_GETZIPCODE,
  API_GETEMPLOYEES,
  API_GETEMPLOYEEBYID,
  API_EDITEMPLOYEE,
} from "../../config/api";
import { api } from "../../config/middleware";
import { useSessionStore } from "../../stores/sessionStore";
import { useAuth } from "../../auth/AuthContext";
import Swal from "sweetalert2";
import LoadingSpinner from "components/LoadingSpinner";

const UserProfile = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const updateProfile = useSessionStore((s) => s.updateProfile);

  const username = user?.username || "";

  const role = useSessionStore((s) => s.profile?.role);
  const orgId = useSessionStore((s) => s.profile?.orgId);
  const brId = useSessionStore((s) => s.profile?.brId);

  // EMPID ของ user ปัจจุบัน (ดึงมาจาก API_GETEMPLOYEES)
  const [empId, setEmpId] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const toPublicFileUrl = (rel) => {
    if (!rel) return "";
    const clean = String(rel)
      .replace(/^uploads[\\/]/, "")
      .replace(/\\/g, "/");
    return `${API_BASE.replace(/\/+$/, "")}/files/${clean}`;
  };

  // ---------- รูป ----------
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");
  const [currentPicture, setCurrentPicture] = useState(""); // path เดิมจาก backend
  const logoInputRef = useRef(null);

  const onLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    const objectUrl = URL.createObjectURL(file);
    setLogoPreview(objectUrl);
    setCurrentPicture(""); // ซ่อนรูปเก่า เวลาเลือกใหม่
  };

  useEffect(() => {
    return () => {
      if (logoPreview && logoPreview.startsWith("blob:")) {
        URL.revokeObjectURL(logoPreview);
      }
    };
  }, [logoPreview]);

  // ---------- ฟอร์ม ----------
  const [form, setForm] = useState({
    EMPUSERNAME: "",
    EMPPASSWORD: "",
    EMPFIRSTNAME: "",
    EMPLASTNAME: "",
    EMPDOB: "",
    EMPIDCARD: "",
    EMPADDR: "",
    EMPADDR2: "",
    EMPSUBDISTRICT: "",
    EMPDISTRICT: "",
    EMPPROVINCE: "",
    EMPZIPCODE: "",
    EMPMOBILE: "",
    EMPEMAIL: "",
    EMPSSOHOSPITAL: "",
    PSID: "",
    BRID: "",
  });

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: value }));
  };

  // ---------- ZIP search ----------
  const [zipQuery, setZipQuery] = useState("");
  const [zipList, setZipList] = useState([]);
  const [zipOpen, setZipOpen] = useState(false);
  const [zipLoading, setZipLoading] = useState(false);
  const zipBoxRef = useRef(null);

  useEffect(() => {
    if (zipQuery === "") {
      setZipList([]);
      setZipOpen(false);
      return;
    }
    const t = setTimeout(() => {
      setZipLoading(true);
      const current = zipQuery;
      api
        .get(API_GETZIPCODE, { params: { zipcode: current } })
        .then((res) => {
          if (current !== zipQuery) return;
          const rows = res?.data?.data ?? [];
          setZipList(rows);
          setZipOpen(true);
        })
        .catch(() => {
          if (current !== zipQuery) return;
          setZipList([]);
          setZipOpen(false);
        })
        .finally(() => {
          if (current !== zipQuery) return;
          setZipLoading(false);
        });
    }, 250);
    return () => clearTimeout(t);
  }, [zipQuery]);

  const onZipChange = (e) => {
    const v = e.target.value;
    setForm((s) => ({ ...s, EMPZIPCODE: v }));
    setZipQuery(v);
  };
  const onPickZip = (item) => {
    setForm((s) => ({
      ...s,
      EMPZIPCODE: String(item.zip_code ?? ""),
      EMPSUBDISTRICT: item.subdistrict_name ?? "",
      EMPDISTRICT: item.district_name ?? "",
      EMPPROVINCE: item.province_name ?? "",
    }));
    setZipOpen(false);
    setZipList([]);
    setZipQuery("");
  };

  // ---------- 1) ดึง EMPID ของ user ปัจจุบันจาก API_GETEMPLOYEES ----------
  useEffect(() => {
    if (!orgId || !username) return;

    let alive = true;
    (async () => {
      setIsLoading(true);
      try {
        const res = await api.get(API_GETEMPLOYEES, {
          params: { ORGID: orgId },
        });

        const raw =
          res?.data?.data?.rows ??
          res?.data?.rows ??
          res?.data?.data ??
          res?.data ??
          [];
        const list = Array.isArray(raw) ? raw : [];

        const matched = list.find((emp) => {
          const empUsername =
            emp.EMPUSERNAME ?? emp.empUsername ?? emp.username ?? "";
          return (
            String(empUsername).toLowerCase() === String(username).toLowerCase()
          );
        });

        if (!alive || !matched) return;

        const id =
          matched.EMPID ?? matched.empId ?? matched.empID ?? matched.id ?? "";
        if (!id) return;

        setEmpId(String(id));
      } catch (err) {
        console.error("API_GETEMPLOYEES error:", err);
      } finally {
        if (alive) setIsLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [orgId, username]);

  // ---------- 2) เมื่อมี empId แล้ว → ดึง detail จาก API_GETEMPLOYEEBYID ----------
  useEffect(() => {
    if (!orgId || !empId) return;
    let alive = true;

    (async () => {
      setIsLoading(true);
      try {
        const res = await api.get(API_GETEMPLOYEEBYID, {
          params: { ORGID: orgId, EMPID: empId },
        });

        const emp = res?.data?.data ?? {};
        if (!alive) return;

        setForm({
          EMPUSERNAME: emp.EMPUSERNAME ?? emp.empUsername ?? emp.username ?? "",
          EMPPASSWORD: "",
          EMPFIRSTNAME:
            emp.EMPFIRSTNAME ?? emp.empFirstname ?? emp.empFirstName ?? "",
          EMPLASTNAME:
            emp.EMPLASTNAME ?? emp.empLastname ?? emp.empLastName ?? "",
          EMPDOB: (emp.EMPDOB ?? emp.empDOB ?? "").substring(0, 10),
          EMPIDCARD: emp.EMPIDCARD ?? emp.empIdCard ?? "",
          EMPADDR: emp.EMPADDR ?? emp.empAddr ?? "",
          EMPADDR2: emp.EMPADDR2 ?? emp.empAddr2 ?? "",
          EMPSUBDISTRICT: emp.EMPSUBDISTRICT ?? emp.empSubDistrict ?? "",
          EMPDISTRICT: emp.EMPDISTRICT ?? emp.empDistrict ?? "",
          EMPPROVINCE: emp.EMPPROVINCE ?? emp.empProvince ?? "",
          EMPZIPCODE: emp.EMPZIPCODE ?? emp.empZipcode ?? emp.empZipCode ?? "",
          EMPMOBILE: emp.EMPMOBILE ?? emp.empMobile ?? "",
          EMPEMAIL: emp.EMPEMAIL ?? emp.empEmail ?? "",
          EMPSSOHOSPITAL: emp.EMPSSOHOSPITAL ?? emp.empSSOHospital ?? "",
          PSID: emp.PSID ?? emp.psId ?? "",
          BRID: emp.BRID ?? emp.brId ?? "",
        });

        const picRel =
          emp.EMPPICTURE ??
          emp.empPicture ??
          emp.EMPPICTUREURL ??
          emp.empPictureUrl ??
          "";

        if (picRel) {
          const publicUrl = picRel.startsWith("http")
            ? picRel
            : toPublicFileUrl(picRel);
          setCurrentPicture(publicUrl);

          updateProfile({
            username: emp.EMPUSERNAME ?? emp.empUsername ?? emp.username ?? "",
            avatar: publicUrl,
          });
        } else {
          updateProfile({
            username: emp.EMPUSERNAME ?? emp.empUsername ?? emp.username ?? "",
          });
        }
      } catch (err) {
        await Swal.fire({
          icon: "error",
          title: "ไม่พบข้อมูลพนักงาน",
          text:
            err?.response?.data?.message || err?.message || "ลองใหม่อีกครั้ง",
        });
      } finally {
        if (alive) setIsLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [orgId, empId, updateProfile]);

  // ---------- Submit (multipart + file) ----------
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      if (!empId) {
        return Swal.fire("ผิดพลาด", "ไม่พบ EMPID", "error");
      }

      const fd = new FormData();
      fd.append("ORGID", orgId);
      fd.append("EMPID", String(empId));
      fd.append("PSID", String(form.PSID));
      fd.append("BRID", String(form.BRID));
      fd.append("EMPUSERNAME", form.EMPUSERNAME);

      // ส่งรหัสผ่านเฉพาะตอนที่ผู้ใช้กรอกใหม่
      if ((form.EMPPASSWORD || "").trim() !== "") {
        fd.append("EMPPASSWORD", form.EMPPASSWORD);
      }

      fd.append("EMPFIRSTNAME", form.EMPFIRSTNAME);
      fd.append("EMPLASTNAME", form.EMPLASTNAME);
      fd.append("EMPDOB", form.EMPDOB);
      fd.append("EMPIDCARD", form.EMPIDCARD);
      fd.append("EMPADDR", form.EMPADDR);
      fd.append("EMPADDR2", form.EMPADDR2);
      fd.append("EMPSUBDISTRICT", form.EMPSUBDISTRICT);
      fd.append("EMPDISTRICT", form.EMPDISTRICT);
      fd.append("EMPPROVINCE", form.EMPPROVINCE);
      fd.append("EMPZIPCODE", form.EMPZIPCODE);
      fd.append("EMPMOBILE", form.EMPMOBILE);
      fd.append("EMPEMAIL", form.EMPEMAIL);
      fd.append("EMPSSOHOSPITAL", form.EMPSSOHOSPITAL);

      if (logoFile) {
        fd.append("EMPPICTURE", logoFile);
      }

      const res = await api.post(API_EDITEMPLOYEE, fd);

      // ถ้า backend ส่ง path รูปใหม่กลับมา
      const updated = res?.data?.data ?? {};
      const newPicRel = updated.EMPPICTURE ?? null;

      const newAvatarUrl = newPicRel
        ? toPublicFileUrl(newPicRel)
        : logoPreview || currentPicture || null;

      // sync ค่าใน zustand ให้ username / avatar ใหม่ไปโผล่ใน AuthContext + Navbar
      updateProfile({
        username: form.EMPUSERNAME,
        ...(newAvatarUrl ? { avatar: newAvatarUrl } : {}),
      });

      Swal.fire({
        icon: "success",
        title: res?.data?.message,
        timer: 1200,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire(
        "บันทึกไม่สำเร็จ",
        err?.response?.data?.message || err.message,
        "error",
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <LoadingSpinner fullPage />;
  }

  return (
    <>
      <Header />
      <Container className="mt--7" fluid>
        <Row>
          <Col xs="12">
            <Card className="bg-secondary shadow">
              <CardHeader className="bg-white border-0">
                <Row className="align-items-center">
                  <Col xs="8">
                    <h6 className="mb-0">ข้อมูลส่วนตัว</h6>
                  </Col>
                </Row>
              </CardHeader>

              <CardBody>
                <Form onSubmit={handleSubmit}>
                  <h6 className="heading-small text-muted mb-4">
                    ข้อมูลพื้นฐาน
                  </h6>

                  {/* รูป */}
                  <Row className="justify-content-center">
                    <Col lg="6" md="8" sm="10" className="mx-auto">
                      <FormGroup className="text-center">
                        <label
                          className="form-control-label d-block"
                          htmlFor="emp-photo-input"
                        >
                          รูปประจำตัว
                        </label>
                        <div className="d-flex align-items-center justify-content-center w-100 mb-3">
                          <div
                            className="rounded-circle bg-light d-inline-flex align-items-center justify-content-center"
                            style={{
                              width: 100,
                              height: 100,
                              overflow: "hidden",
                              border: "1px solid #e9ecef",
                              cursor: "pointer",
                            }}
                            onClick={() => logoInputRef.current?.click()}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) =>
                              (e.key === "Enter" || e.key === " ") &&
                              logoInputRef.current?.click()
                            }
                            aria-label="อัปโหลดรูปพนักงาน"
                          >
                            {logoPreview ? (
                              <img
                                alt="ตัวอย่างรูปพนักงาน"
                                src={logoPreview}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                            ) : currentPicture ? (
                              <img
                                alt="รูปพนักงานปัจจุบัน"
                                src={currentPicture}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                            ) : (
                              <span className="text-muted small">เลือกรูป</span>
                            )}
                          </div>
                        </div>
                        <Input
                          innerRef={logoInputRef}
                          id="emp-photo-input"
                          type="file"
                          accept="image/*"
                          onChange={onLogoChange}
                          className="d-none"
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <div className="pl-lg-4">
                    {/* ชื่อ-นามสกุล */}
                    <Row>
                      <Col lg="6">
                        <FormGroup>
                          <label className="form-control-label">ชื่อ</label>
                          <Input
                            name="EMPFIRSTNAME"
                            value={form.EMPFIRSTNAME}
                            onChange={onChange}
                            type="text"
                            className="form-control-alternative"
                          />
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <label className="form-control-label">นามสกุล</label>
                          <Input
                            name="EMPLASTNAME"
                            value={form.EMPLASTNAME}
                            onChange={onChange}
                            type="text"
                            className="form-control-alternative"
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    {/* Username/Password */}
                    <Row>
                      <Col lg="6">
                        <FormGroup>
                          <label className="form-control-label">Username</label>
                          <Input
                            name="EMPUSERNAME"
                            value={form.EMPUSERNAME}
                            onChange={onChange}
                            type="text"
                            className="form-control-alternative"
                          />
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <div className="d-flex justify-content-between">
                            <label className="form-control-label">
                              Password
                            </label>
                            <small className="text-muted">
                              ปล่อยว่างหากไม่ต้องการเปลี่ยน
                            </small>
                          </div>
                          <Input
                            name="EMPPASSWORD"
                            value={form.EMPPASSWORD}
                            onChange={onChange}
                            type="text"
                            className="form-control-alternative"
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    {/* วันเกิด / รพ. */}
                    <Row>
                      <Col lg="6">
                        <FormGroup className="mb-0">
                          <label className="form-control-label">
                            วันเดือนปีเกิด
                          </label>
                          <Input
                            type="date"
                            name="EMPDOB"
                            value={form.EMPDOB}
                            onChange={onChange}
                            className="form-control-alternative"
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <hr className="my-4" />
                    <h6 className="heading-small text-muted mb-4">
                      ช่องทางการติดต่อ
                    </h6>

                    {/* ที่อยู่ */}
                    <Row>
                      <Col lg="6">
                        <FormGroup>
                          <label className="form-control-label">ที่อยู่</label>
                          <Input
                            name="EMPADDR"
                            value={form.EMPADDR}
                            onChange={onChange}
                            type="text"
                            className="form-control-alternative"
                          />
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <label className="form-control-label">
                            ที่อยู่เพิ่มเติม
                          </label>
                          <Input
                            name="EMPADDR2"
                            value={form.EMPADDR2}
                            onChange={onChange}
                            type="text"
                            className="form-control-alternative"
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    {/* Zip + ตำบล/อำเภอ/จังหวัด */}
                    <Row>
                      <Col lg="3">
                        <FormGroup
                          style={{ position: "relative" }}
                          innerRef={zipBoxRef}
                        >
                          <label className="form-control-label">
                            รหัสไปรษณีย์
                          </label>
                          <Input
                            name="EMPZIPCODE"
                            value={form.EMPZIPCODE}
                            onChange={onZipChange}
                            onFocus={() => zipList.length && setZipOpen(true)}
                            autoComplete="off"
                            type="text"
                            className="form-control-alternative"
                          />
                          {zipOpen && (
                            <div
                              className="dropdown-menu show w-100"
                              style={{
                                position: "absolute",
                                top: "100%",
                                left: 0,
                                zIndex: 1050,
                                maxHeight: 240,
                                overflowY: "auto",
                              }}
                            >
                              {zipLoading && (
                                <div className="dropdown-item text-muted small">
                                  กำลังค้นหา…
                                </div>
                              )}
                              {!zipLoading && zipList.length === 0 && (
                                <div className="dropdown-item text-muted small">
                                  ไม่พบข้อมูลรหัสนี้
                                </div>
                              )}
                              {!zipLoading &&
                                zipList.map((it, idx) => (
                                  <button
                                    key={idx}
                                    type="button"
                                    className="dropdown-item d-flex flex-column align-items-start"
                                    onClick={() => onPickZip(it)}
                                  >
                                    <div>
                                      <strong>{it.zip_code}</strong>
                                    </div>
                                    <div className="small text-muted">
                                      {it.subdistrict_name} • {it.district_name}{" "}
                                      • {it.province_name}
                                    </div>
                                  </button>
                                ))}
                            </div>
                          )}
                        </FormGroup>
                      </Col>

                      <Col lg="3">
                        <FormGroup>
                          <label className="form-control-label">
                            ตำบล/แขวง
                          </label>
                          <Input
                            disabled
                            name="EMPSUBDISTRICT"
                            value={form.EMPSUBDISTRICT}
                            onChange={onChange}
                            type="text"
                            className="form-control-alternative"
                          />
                        </FormGroup>
                      </Col>

                      <Col lg="3">
                        <FormGroup>
                          <label className="form-control-label">
                            อำเภอ/เขต
                          </label>
                          <Input
                            disabled
                            name="EMPDISTRICT"
                            value={form.EMPDISTRICT}
                            onChange={onChange}
                            type="text"
                            className="form-control-alternative"
                          />
                        </FormGroup>
                      </Col>

                      <Col lg="3">
                        <FormGroup>
                          <label className="form-control-label">จังหวัด</label>
                          <Input
                            disabled
                            name="EMPPROVINCE"
                            value={form.EMPPROVINCE}
                            onChange={onChange}
                            type="text"
                            className="form-control-alternative"
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    {/* เบอร์/อีเมล */}
                    <Row>
                      <Col lg="6">
                        <FormGroup>
                          <label className="form-control-label">เบอร์โทร</label>
                          <Input
                            name="EMPMOBILE"
                            value={form.EMPMOBILE}
                            onChange={(e) => {
                              const onlyNums = e.target.value.replace(
                                /[^0-9]/g,
                                "",
                              );
                              setForm((s) => ({ ...s, EMPMOBILE: onlyNums }));
                            }}
                            inputMode="numeric"
                            pattern="[0-9]*"
                            maxLength={10}
                            type="text"
                            className="form-control-alternative"
                          />
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <label className="form-control-label">Email</label>
                          <Input
                            name="EMPEMAIL"
                            value={form.EMPEMAIL}
                            onChange={onChange}
                            type="email"
                            className="form-control-alternative"
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <ModalFooter>
        <Button color="secondary" onClick={() => navigate("/index")}>
          ยกเลิก
        </Button>
        <Button color="warning" type="submit" onClick={handleSubmit}>
          อัปเดต
        </Button>
      </ModalFooter>
    </>
  );
};

export default UserProfile;
